import React, { useEffect, useState } from 'react';
import { Container, Col, Row } from 'react-grid-system';
import { Link } from 'react-router-dom';

import _ from 'lodash';
import axios from 'axios';

import { useApp } from '../_libs/hooks/useApp';
import { useBrand } from '../_libs/hooks/useBrand';
import { useData } from '../_libs/hooks/useData';

import DataColumn from '../atoms/DataColumn';
import ImageFromAzureWithUri from '../atoms/ImageFromAzureWithUri';
import DataBlurb from '../molecules/data/DataBlurb';
import Navigation from '../molecules/Navigation';
import ErrorBoundary from '../organisms/ErrorBoundary';
import Map from '../organisms/Map/Map';
import NPIsGraph from '../organisms/NPIsGraph';
import PrimaryData from '../organisms/PrimaryData';
import TopRiskDriverDisplay from '../organisms/TopRiskDriverDisplay';
import { RaceEthnicityType } from '_libs/data/types';
import { OrganizationBrandData } from '_libs/app/types';

interface ZipCodePageType {
  radius?: 1 | 10 | 100;
}

// TODO: Move to useData
const fetchZIP = (zip, setZipData) => {
  axios
    .get(`/data_zip/${zip}/`)
    .then((res) => {
      const data = res.data;
      if (data.status == 'OK') {
        setZipData(data.zip);
      }
    })
    .catch((err) => {
      setZipData(null);
      console.error(err);
    });
};

const fetchBlockGroupsDataForGeoIds = (geoIds, setBlockGroupsInView) => {
  axios
    .post('/data_block_groups_for_geoids/', { geoIds })
    .then((res) => {
      const data = res.data;
      if (data.status == 'OK') {
        setBlockGroupsInView(data.blockGroups);
      }
    })
    .catch((err) => {
      console.error(err);
    })
    .then(() => {
      return 'Not sure what to do here.';
    });
};

function getMedian(arr: number[]) {
  if(arr.length % 2) {
    return arr[Math.floor(arr.length / 2)];
  } else {
    const center = arr.length / 2;
    return (arr[center-1] + arr[center]) / 2;
  }
}

function addPercentOfTotalPopulation (ad: AggregateDataType, bg: BlockGroupType, totalPopulation: number, colLabel: string) {
  ad[`${colLabel}`] += totalPopulation * bg[`${colLabel}`];
}

const handleBlockGroupsInViewChange = (blockGroupsInView) => {
  const ad: AggregateDataType = {
    count: 0,
    household_est: 0,
    totalpopulation_est: 0,
    ep_age17_label: -1,
    ep_age17_value: -1,
    ep_age65_label: -1,
    ep_age65_value: -1,
    ep_limeng_label: -1,
    ep_limeng_value: -1,
    ep_uninsur_label: -1,
    ep_uninsur_value: -1,
    eqi_land_walkability_label: -1,
    eqi_land_walkability_value: -1,
    ethnicity_hispanic: -1,
    plcr_cervical_label: -1,
    plcr_cervical_value: -1,
    plcr_colon_screen_label: -1,
    plcr_colon_screen_value: -1,
    plcr_mammouse_label: -1,
    plcr_mammouse_value: -1,
    poverty_belowpoverty_label: -1,
    poverty_belowpoverty_value: -1,
    race_asian_pi: -1,
    race_black: -1,
    race_native_amer: -1,
    race_other: -1,
    race_white: -1,
  };
  const walkabilityValues = [];
  _.map(blockGroupsInView, (bg: BlockGroupType)  => {
    const bgTotalPopulation = bg.totalpopulation_est;
    // Sum simple stats
    ad.count += 1;
    ad.household_est += bg.household_est;
    ad.totalpopulation_est += bgTotalPopulation;

    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'ep_age17_value'); // Stored in DB as a percent, or whole number
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'ep_age65_value'); // Stored in DB as a percent, or whole number
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'ep_limeng_value'); // Stored in DB as a percent, or whole number
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'ep_uninsur_value'); // Stored in DB as a percent, or whole number

    walkabilityValues.push(bg.eqi_land_walkability_value);

    // Get approximate sums of the following rates
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'ethnicity_hispanic');
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'plcr_cervical_value'); // Stored in DB as a percent, or whole number
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'plcr_colon_screen_value'); // Stored in DB as a percent, or whole number
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'plcr_mammouse_value'); // Stored in DB as a percent, or whole number
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'poverty_belowpoverty_value'); // Stored in DB as a percent, or whole number
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'race_asian_pi');
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'race_black');
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'race_native_amer');
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'race_other');
    addPercentOfTotalPopulation(ad, bg, bgTotalPopulation, 'race_white');
  });

  const walkabilityMedian = getMedian(walkabilityValues);

  const adTotalPopulation = ad.totalpopulation_est;

  const _ad: AggregateDataType = {
    count: ad.count,
    household_est: ad.household_est,
    totalpopulation_est: ad.totalpopulation_est,
    ep_age17_label: -1,
    ep_age17_value: ad.ep_age17_value / adTotalPopulation,
    ep_age65_label: -1,
    ep_age65_value: ad.ep_age65_value / adTotalPopulation,
    ep_limeng_label: -1,
    ep_limeng_value: ad.ep_limeng_value / adTotalPopulation,
    ep_uninsur_label: -1,
    ep_uninsur_value: ad.ep_uninsur_value / adTotalPopulation,
    eqi_land_walkability_label: -1,
    eqi_land_walkability_value: walkabilityMedian,
    ethnicity_hispanic: ad.ethnicity_hispanic / adTotalPopulation,
    plcr_cervical_label: -1,
    plcr_cervical_value: ad.plcr_cervical_value / adTotalPopulation,
    plcr_colon_screen_label: -1,
    plcr_colon_screen_value: ad.plcr_colon_screen_value / adTotalPopulation,
    plcr_mammouse_label: -1,
    plcr_mammouse_value: ad.plcr_mammouse_value / adTotalPopulation,
    poverty_belowpoverty_label: -1,
    poverty_belowpoverty_value: ad.poverty_belowpoverty_value / adTotalPopulation,
    race_asian_pi: ad.race_asian_pi / adTotalPopulation,
    race_black: ad.race_black / adTotalPopulation,
    race_native_amer: ad.race_native_amer / adTotalPopulation,
    race_other: ad.race_other / adTotalPopulation,
    race_white: ad.race_white / adTotalPopulation,
  };
  return _ad;
};

const DashboardPage: React.FC<ZipCodePageType> = () => {
  const {
    geoIds,
    geoId,
    zipCode,
    radius,
  } = useApp();
  const { reportData, setTopRaceEthnicities, setReportData } = useData();
  const { primaryBrand, coBrand, isValidBrand, getColors } = useBrand();

  const [brand, setBrand] = useState<OrganizationBrandData>(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [zipData, setZipData] = useState<ZIPType>(null);
  const [blockGroupsInView, setBlockGroupsInView] = useState(null);
  const [aggregatedData, setAggregatedData] = useState<AggregateDataType>(null);
  const [showBiasStatement, toggleBiasStatement] = useState<boolean>(false);
  const colors = getColors();

  // When component mounts
  useEffect(() => {
    fetchZIP(zipCode, setZipData);
    document.body.style.backgroundColor = colors.light;
    document.body.style.color = colors.dark;
    document.getElementById('body-gradient').style.opacity = '1';
    document.getElementById('js-bg').style.opacity = '0.25';
  }, []);

  useEffect(() => {
    if (isValidBrand(primaryBrand)) {
      setBrand(primaryBrand);
    }
    else if (isValidBrand(coBrand)) {
      setBrand(coBrand);
    }
    setLogoUrl((primaryBrand.logoUrl) ? primaryBrand.logoUrl : coBrand.logoUrl);
  }, [coBrand, primaryBrand]);

  useEffect(() => {
    // TODO: Check that this is a new group of geoIds
    fetchBlockGroupsDataForGeoIds(geoIds, setBlockGroupsInView);
  }, [geoIds]);

  // NOTE: This probably does not need a hook, simply update aggregated drivers in [riskDriversForGeoIds]
  useEffect(() => {
    const newAggregatedData = handleBlockGroupsInViewChange(blockGroupsInView);
    setAggregatedData(newAggregatedData);
    const _reportData = _.assign(reportData, newAggregatedData);
    setReportData(_reportData);
  }, [blockGroupsInView]);

  useEffect(() => {
    // TODO: Show container after data has loaded

    // TODO: Should this belong in useData?
    if (aggregatedData) {
      const raceEthnicities = [{
        column: 'race_asian_pi', 
        value: aggregatedData.race_asian_pi,
      },
      {
        column: 'race_black',
        value: aggregatedData.race_black,
      },
      {
        column: 'race_native_amer',
        value: aggregatedData.race_native_amer,
      },
      {
        column: 'race_other',
        value: aggregatedData.race_other,
      },
      {
        column: 'race_white',
        value: aggregatedData.race_white,
      },
      {
        column: 'ethnicity_hispanic',
        value: aggregatedData.ethnicity_hispanic,
      }];
      const topRaceEthnicities: RaceEthnicityType[] = [];
      _.map(
        _.orderBy(raceEthnicities, 'value', 'desc'),
        (raceEthnicity) => {
          topRaceEthnicities.push(raceEthnicity.column as RaceEthnicityType);
        }
      );
      setTopRaceEthnicities(_.slice(topRaceEthnicities, 0, 3)); // Limit to top 3 race/ethnicity
    }
  }, [aggregatedData]);

  const data: AggregateDataType = aggregatedData;

  return (
    data &&
      <ErrorBoundary>
        <Navigation />
        <div
          style={{
            backgroundColor: 'white',
            color: 'inherit',
          }}
        >
          <Container>
            <Row align="center">
              <Col sm={7} className="py-4">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  <div>
                    {
                      logoUrl?.length &&
                        <ImageFromAzureWithUri
                          className="img-fit" style={{
                            maxHeight: '72px',
                            maxWidth: '220px',
                            marginRight: '2rem',
                          }}
                          fileUri={logoUrl}
                          alt={`Logo for ${brand.name}`}
                        />
                    }
                  </div>
                  <div>
                    <div className="f-mono f-small">Dashboard for</div>
                    <h1 style={{
                      display: 'inline-block',
                      margin: 0,
                    }}>
                      {primaryBrand?.name ? primaryBrand.name : 'Enter Organization Name'}
                      <span className="f-serif f-small">
                        &nbsp;<Link className="f-blue" to="/organization/">Edit</Link>
                      </span>
                    </h1>
                    <div>
                      Viewing insights for neighborhoods within a <Link className="f-blue" to="/radius/">{radius}-mile</Link> radius of <Link className="f-blue" to="/zip/">{zipCode}</Link>.
                    </div>
                  </div>
                </div>
              </Col>
              <Col sm={5}>
                <DataColumn style={{
                  marginTop: '15px',
                  marginBottom: '15px',
                }}>
                  <Row>
                    <Col>
                      <DataBlurb
                        value={Intl.NumberFormat('en-US').format(data.count)}
                        label="Block Groups"
                      /></Col>
                    <Col>
                      <DataBlurb
                        value={Intl.NumberFormat('en-US').format(data.household_est)}
                        label="Estimated Households"
                      />
                    </Col>
                    <Col>
                      <DataBlurb
                        value={Intl.NumberFormat('en-US').format(data.totalpopulation_est)}
                        label="Estimated Population"
                      />
                    </Col>
                  </Row>
                </DataColumn>
              </Col>
            </Row>
          </Container>
        </div>
        <Container style={{
          paddingBottom: '4rem',
        }}>
          <Row>
            <Col sm={7}>
              <ErrorBoundary>
                <PrimaryData data={data} />
              </ErrorBoundary>
              <ErrorBoundary>
                <NPIsGraph geoId={`${geoId}`} radius={radius} zipCode={zipCode} />
              </ErrorBoundary>
              <ErrorBoundary>
                <TopRiskDriverDisplay geoIds={geoIds} />
              </ErrorBoundary>
            </Col>
            <Col md={5}>
              <ErrorBoundary>
                <h3>Community Map</h3>
                { zipData && (
                    <Map radius={radius} latitude={zipData.latitude} longitude={zipData.longitude} />
                  )}
              </ErrorBoundary>
              <div className="mt-2">
                <div className="t-right" style={{
                  opacity: 0.75
                }}>
                  <button
                    className="f-sans f-small bare"
                    onClick={() => {
                      toggleBiasStatement(!showBiasStatement);
                    }}
                  >
                    {
                      !showBiasStatement 
                        ? 'Data & AI Integrity'
                        : 'Close Data & AI Integrity'
                    }
                  </button>
                </div>
                {
                  showBiasStatement && 
                    <div className="mt-4">
                      <DataColumn>
                        <div className="f-sans f-small pa-2">
                          <h4 className="mt-0">Data & AI Integrity</h4>
                          We are committed to sourcing the least-biased raw data possible, acknowledging that all data may suffer from generalizations or erasure. We are committed to minimizing correlative carry-over and bias-worsening when creating predictive models by maintaining the fidelity of starting data. And we are committed to building models that stand to benefit, and not harm, the communities or individuals that they serve, while being designed to prevent stigmatization or implicit bias, and while adhering to contemporary clinical guidelines.
                        </div>
                      </DataColumn>
                    </div>
                }
              </div>
            </Col>
          </Row>
        </Container>
      </ErrorBoundary>
  );
};

export default DashboardPage;
